import type { AxiosError } from "axios";
import i18n from "i18next";

import { queryClient } from "~/lib/queryClient";
import { ResponseError } from "~/utils/ResponseError";

import { ME_ACCESS_QUERY_KEY } from "./queryKeys";

export const FORBIDDEN_STATUS = 403;

/**
 * The server's authorization refusals answer 403 with a Problem Details body
 * whose `type` is this prefix plus the reason.
 */
export const AUTHZ_TYPE_PREFIX = "urn:meq:authz:";

export const AUTHZ_REASONS = [
  "feature-missing",
  "scope-empty",
  "scope-violation",
  "action-forbidden",
  "export-limit",
  "org-unmapped",
  "place-unnamed",
] as const;

export type AuthzReason = (typeof AUTHZ_REASONS)[number];

export interface AuthzProblem {
  type: string;
  title: string;
  dataset?: string;
  action?: string;
  feature?: string;
  org?: string;
  location?: string;
  column?: string;
}

export interface AuthzRoute {
  /** The payload that let the UI make this call is out of date. */
  shouldRefreshAccess: boolean;
  /** Something on screen already explains the refusal. */
  isSilent: boolean;
}

/**
 * A missing feature or action means the payload the UI rendered from has gone
 * stale, so it is refetched and the screen redraws without the item. An empty
 * scope is shown by the empty-scope screen once the refetched payload flags
 * it. The rest are a request the UI built wrongly or too wide, and only say so.
 */
export const AUTHZ_ROUTES: Record<AuthzReason, AuthzRoute> = {
  "feature-missing": { shouldRefreshAccess: true, isSilent: false },
  "action-forbidden": { shouldRefreshAccess: true, isSilent: false },
  "scope-empty": { shouldRefreshAccess: true, isSilent: true },
  "scope-violation": { shouldRefreshAccess: false, isSilent: false },
  "export-limit": { shouldRefreshAccess: false, isSilent: false },
  "org-unmapped": { shouldRefreshAccess: false, isSilent: false },
  "place-unnamed": { shouldRefreshAccess: false, isSilent: false },
};

export class AuthzError extends ResponseError {
  reason: AuthzReason;
  problem: AuthzProblem;
  isSilent: boolean;

  constructor(reason: AuthzReason, problem: AuthzProblem) {
    super(i18n.t(`access:refusal.${reason}`), FORBIDDEN_STATUS);
    this.reason = reason;
    this.problem = problem;
    this.isSilent = AUTHZ_ROUTES[reason].isSilent;
  }
}

export const authzReasonOf = (type: unknown): AuthzReason | undefined => {
  if (typeof type !== "string" || !type.startsWith(AUTHZ_TYPE_PREFIX)) {
    return undefined;
  }
  const reason = type.slice(AUTHZ_TYPE_PREFIX.length);
  return (AUTHZ_REASONS as readonly string[]).includes(reason)
    ? (reason as AuthzReason)
    : undefined;
};

/**
 * Routes an authorization refusal on its Problem Details `type`, or returns
 * null so the caller falls through to its normal error mapping.
 */
export const handleAuthzRefusal = (
  error: AxiosError,
): Promise<never> | null => {
  if (error.response?.status !== FORBIDDEN_STATUS) return null;
  const problem = error.response.data as AuthzProblem | undefined;
  const reason = authzReasonOf(problem?.type);
  if (!reason || !problem) return null;

  if (AUTHZ_ROUTES[reason].shouldRefreshAccess) {
    void queryClient.invalidateQueries({ queryKey: ME_ACCESS_QUERY_KEY });
  }
  return Promise.reject(new AuthzError(reason, problem));
};
